import { AbstractControl, ValidationErrors } from '@angular/forms';

export const AUTH_LIMITS = {
  emailMax: 254,
  passwordMin: 12,
  passwordMax: 128,
  mfaLength: 6,
  inviteTokenMin: 16,
  inviteTokenMax: 256,
  errorMessageMax: 280,
  maxFailedAttempts: 5,
  lockoutSeconds: 30
} as const;

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const CONTROL_CHARS = /[\u0000-\u001F\u007F]/;
const INVITE_TOKEN_PATTERN = /^[A-Za-z0-9_-]+$/;
const MFA_PATTERN = /^\d{6}$/;
const HTML_OR_SCRIPT = /<[^>]*>|javascript:|data:text\/html|on\w+\s*=/i;

export function normalizeEmail(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

export function sanitizeUserMessage(raw: unknown, fallback: string): string {
  if (typeof raw !== 'string') {
    return fallback;
  }

  if (HTML_OR_SCRIPT.test(raw)) {
    return fallback;
  }

  const text = raw
    .replace(/<[^>]*>/g, '')
    .replace(CONTROL_CHARS, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!text || text.length > AUTH_LIMITS.errorMessageMax) {
    return fallback;
  }

  return text;
}

export function isValidEmail(value: string): boolean {
  const email = normalizeEmail(value);
  if (!email || email.length > AUTH_LIMITS.emailMax) {
    return false;
  }
  if (email.includes('..') || email.startsWith('.') || email.endsWith('.')) {
    return false;
  }
  const [local, domain] = email.split('@');
  if (!local || !domain || local.startsWith('.') || local.endsWith('.') || domain.startsWith('-') || domain.endsWith('-')) {
    return false;
  }
  return EMAIL_PATTERN.test(email);
}

export function passwordIssues(value: unknown): string[] {
  if (typeof value !== 'string' || value.length === 0) {
    return ['required'];
  }
  if (CONTROL_CHARS.test(value)) {
    return ['unsafe'];
  }
  const issues: string[] = [];
  if (value.length < AUTH_LIMITS.passwordMin) {
    issues.push('minlength');
  }
  if (value.length > AUTH_LIMITS.passwordMax) {
    issues.push('maxlength');
  }
  if (!/[A-Za-z]/.test(value)) {
    issues.push('letter');
  }
  if (!/\d/.test(value)) {
    issues.push('digit');
  }
  return issues;
}

export function sanitizeMfaCode(value: unknown): string {
  return typeof value === 'string' ? value.replace(/\D/g, '').slice(0, AUTH_LIMITS.mfaLength) : '';
}

export function isValidMfaCode(value: string): boolean {
  return MFA_PATTERN.test(value);
}

export function sanitizeInviteToken(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function isValidInviteToken(value: string): boolean {
  return (
    value.length >= AUTH_LIMITS.inviteTokenMin &&
    value.length <= AUTH_LIMITS.inviteTokenMax &&
    INVITE_TOKEN_PATTERN.test(value)
  );
}

export function parseTenantId(value: unknown, allowedIds: number[] = []): number | undefined {
  const id = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(id) || id <= 0) {
    return undefined;
  }
  if (allowedIds.length > 0 && !allowedIds.includes(id)) {
    return undefined;
  }
  return id;
}

export function isSafeMicrosoftAuthorizeUrl(url: unknown): boolean {
  if (typeof url !== 'string') {
    return false;
  }
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    return (
      parsed.protocol === 'https:' &&
      (host === 'login.microsoftonline.com' || host.endsWith('.login.microsoftonline.com'))
    );
  } catch {
    return false;
  }
}

export function emailValidator(control: AbstractControl): ValidationErrors | null {
  const value = typeof control.value === 'string' ? control.value.trim() : '';
  if (!value) {
    return { required: true };
  }
  if (value.length > AUTH_LIMITS.emailMax) {
    return { maxlength: true };
  }
  return isValidEmail(value) ? null : { email: true };
}

export function passwordValidator(control: AbstractControl): ValidationErrors | null {
  const issues = passwordIssues(control.value);
  if (issues.length === 0) {
    return null;
  }
  return issues.reduce((errors, issue) => ({ ...errors, [issue]: true }), {} as ValidationErrors);
}

export function requiredTenantValidator(control: AbstractControl): ValidationErrors | null {
  const id = parseTenantId(control.value);
  return id ? null : { required: true };
}
